import { useMemo } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowParams,
} from '@mui/x-data-grid'
import { formatDisplayDate } from '~/shared/lib/date'
import type { PolicyConfigurationRow } from './BenefitPolicyService'
import { PolicyStatusPill } from './PolicyStatusPill'

const formatPeriod = (start: string | null, end: string | null) => {
  if (!start && !end) return '—'
  return `${start ? formatDisplayDate(start) : '—'} – ${end ? formatDisplayDate(end) : '—'}`
}

interface BenefitPolicyListProps {
  rows: PolicyConfigurationRow[]
  loading: boolean
  onOpen: (id: number) => void
  onClone: (row: PolicyConfigurationRow) => void
}

export const BenefitPolicyList = ({ rows, loading, onOpen, onClone }: BenefitPolicyListProps) => {
  const columns = useMemo<GridColDef<PolicyConfigurationRow>[]>(
    () => [
      {
        field: 'contract_name',
        headerName: 'Contract',
        flex: 1.5,
        minWidth: 180,
        renderCell: ({ row }: GridRenderCellParams<PolicyConfigurationRow>) => (
          <Box sx={{ minWidth: 0, py: 1 }}>
            <Typography sx={{ fontWeight: 600 }} noWrap>
              {row.contract_name ?? '—'}
            </Typography>
            {/* Resolved server-side through the contract's generic FK: the same contract name can
                mean an employer or a payer, so the row says which. */}
            <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
              {row.contracted_entity_label}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'effective_start',
        headerName: 'Effective period',
        flex: 1.3,
        minWidth: 190,
        valueFormatter: (_value, row) => formatPeriod(row.effective_start, row.effective_end),
      },
      {
        field: 'electable_start_date',
        headerName: 'Electable from',
        flex: 1,
        minWidth: 130,
        valueFormatter: (value: string | null) => (value ? formatDisplayDate(value) : '—'),
      },
      {
        field: 'coverage_product_name',
        headerName: 'Coverage product',
        flex: 1.2,
        minWidth: 150,
        valueFormatter: (value: string | null) => value ?? '—',
      },
      {
        field: 'accumulator_tiers',
        headerName: 'Accumulator tiers',
        width: 130,
        // A string enum ("2" | "3"), not a number — the choices come through as strings.
        valueFormatter: (value: string | null) => (value == null ? '—' : `${value}-tier`),
      },
      {
        field: 'display_state',
        headerName: 'Status',
        width: 120,
        sortable: true,
        renderCell: ({ row }: GridRenderCellParams<PolicyConfigurationRow>) => (
          <PolicyStatusPill displayState={row.display_state} testId={`policy-status-${row.id}`} />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }: GridRenderCellParams<PolicyConfigurationRow>) =>
          // `can_clone` already folds in the operator's write permission and whether the row has a
          // start date to shift from, so the button's presence is the server's answer.
          row.can_clone ? (
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={event => {
                // The row itself opens the editor; the button must not do both.
                event.stopPropagation()
                onClone(row)
              }}
              data-testid={`clone-policy-${row.id}`}
            >
              Clone
            </Button>
          ) : null,
      },
    ],
    [onClone],
  )

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      loading={loading}
      onRowClick={(params: GridRowParams<PolicyConfigurationRow>) => onOpen(params.row.id)}
      disableRowSelectionOnClick
      getRowHeight={() => 'auto'}
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      pageSizeOptions={[25, 50, 100]}
      slotProps={{
        noRowsOverlay: { 'aria-label': 'No policies' },
      }}
      sx={{
        bgcolor: 'background.paper',
        '& .MuiDataGrid-row': { cursor: 'pointer' },
        // `getRowHeight: auto` needs the cell to own its vertical padding.
        '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: 0.5 },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
        '& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '-2px',
        },
      }}
      data-testid="benefit-policy-list"
    />
  )
}

const NO_POLICIES_MESSAGE = 'No policies match this filter.'

/** Empty state shown in place of the grid, so the message is not buried in an overlay. */
export const BenefitPolicyListEmpty = () => (
  <Stack sx={{ alignItems: 'center', py: 4 }}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {NO_POLICIES_MESSAGE}
    </Typography>
  </Stack>
)
